import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { PackingAssistant } from '@/components/features/PackingAssistant';
import { CurrencyConverterWithScanner } from '@/components/features/CurrencyConverter';
import { HealthWellness } from '@/components/features/HealthWellness';
import { CombinedLocalTips } from '@/components/features/CombinedLocalTips';
import { SeasonalRecommendations } from '@/components/features/SeasonalRecommendations';

import { OnboardingFlow } from '@/components/onboarding/OnboardingFlow';
import { SocialFollow } from '@/components/ui/social-follow';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { DemoVideo } from '@/components/demo/DemoVideo';
import { TutorialTooltip } from '@/components/ui/tutorial-tooltip';
import { DollarSign, Heart, Globe, Luggage, Cloud, Calendar, Sparkles } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOnboarding } from '@/hooks/useOnboarding';
import { VoyagerButton } from '@/components/ui/voyager-button';
import { useSubscription } from '@/hooks/useSubscription';
import iphoneMockup from '/lovable-uploads/ebcf5963-0851-4008-bd45-e8f564e73dc8.png';
import { Logo } from '@/components/ui/Logo';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { FloatingChatButton } from '@/components/chat/FloatingChatButton';
import { openCookieSettings } from '@/components/ui/cookie-consent-banner';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'packing';
  
  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };
  const {
    needsOnboarding,
    loading,
    completeOnboarding
  } = useOnboarding();
  const {
    hasFeature
  } = useSubscription();
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  // Download app button hidden until native apps are ready
  // const handleDownloadApp = () => {
  //   const userAgent = navigator.userAgent || navigator.vendor;
  //   if (/iPad|iPhone|iPod/.test(userAgent)) {
  //     window.open('https://apps.apple.com/app/odyvoy', '_blank');
  //   } else if (/android/i.test(userAgent)) {
  //     window.open('https://play.google.com/store/apps/details?id=app.lovable.odyvoy', '_blank');
  //   } else {
  //     window.open('https://apps.apple.com/app/odyvoy', '_blank');
  //   }
  // };
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>;
  }
  if (needsOnboarding) {
    return <OnboardingFlow onComplete={completeOnboarding} />;
  }
  return <>
      <Helmet>
        <title>Dashboard | OdyVoy - Your AI Travel Companion</title>
        <meta name="description" content="Plan your trips with OdyVoy's AI-powered dashboard. Access smart packing lists, currency converter, health tips, and local insights." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://odyvoy.com/dashboard" />
        <meta property="og:title" content="Your OdyVoy Dashboard — AI Trip Planning & Smart Packing" />
        <meta property="og:description" content="Your personal AI travel hub: smart packing lists, currency tools, health tips, and local insights for every trip." />
        <meta property="og:url" content="https://odyvoy.com/dashboard" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-background">
      
      {/* Navigation Header */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Logo className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20" alt="OdyVoy Logo" />
            </div>
            <nav className="hidden md:flex space-x-8">
            </nav>
            <div className="flex items-center space-x-4">
              {/* Download app button hidden until native apps are ready */}
              <TutorialTooltip id="notifications" title="Weather & Travel Alerts" description="Get notified about weather changes, severe weather warnings, and important travel reminders">
                <NotificationBell />
              </TutorialTooltip>
              <ThemeToggle />
              <TutorialTooltip id="voyager-account" title="Your Account" description="Manage your subscription, preferences, and saved content here.">
                <VoyagerButton variant="default" size="icon" className="h-8 w-8 md:h-10 md:w-10 bg-primary hover:bg-primary/90 text-primary-foreground" />
              </TutorialTooltip>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Welcome Section - matches landing aesthetic */}
      <section className="relative overflow-hidden">
        {/* Animated gradient blobs */}
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-72 h-72 rounded-full bg-primary/20 blur-3xl animate-blob-float" />
          <div
            className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-accent/20 blur-3xl animate-blob-float"
            style={{ animationDelay: '2s' }}
          />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full bg-gradient-to-br from-primary/5 via-transparent to-accent/5 blur-3xl"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 rounded-full border border-primary/20 bg-primary/5 backdrop-blur-sm text-xs sm:text-sm font-medium text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            Your AI Travel Companion
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4 leading-[1.1]">
            <span className="bg-gradient-to-r from-foreground via-foreground to-foreground/60 bg-clip-text text-transparent">
              Welcome back.
            </span>
            <br />
            <span className="bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent">
              Where to next?
            </span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Pick a tool below to plan, pack and explore — all powered by AI.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16" id="features">

        {/* Modern Feature Tabs */}
        <div className="max-w-6xl mx-auto">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            {/* Pill-style Tab Navigation */}
            <div className="mb-6 sm:mb-8">
              <TabsList className="w-full justify-start sm:justify-center bg-transparent h-auto p-0 gap-2 sm:gap-3 flex-nowrap overflow-x-auto scrollbar-thin">
                {[
                  { value: 'packing', icon: Luggage, label: 'Packing', gradient: 'from-cyan-400 to-teal-500', show: true },
                  { value: 'currency', icon: DollarSign, label: 'Currency', gradient: 'from-cyan-500 to-teal-500', show: true },
                  { value: 'health', icon: Heart, label: 'Health', gradient: 'from-cyan-500 to-teal-500', show: true },
                  { value: 'local-tips', icon: Globe, label: 'Local Tips', gradient: 'from-cyan-500 to-teal-500', show: true },
                  { value: 'seasonal', icon: Calendar, label: 'Best Time', gradient: 'from-emerald-400 to-green-500', show: hasFeature('seasonal_recommendations') },
                  
                ].filter((t) => t.show).map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="group relative flex items-center gap-2 rounded-full border border-border bg-card px-4 sm:px-5 py-2.5 sm:py-3 text-sm font-medium text-muted-foreground transition-all duration-300 hover:border-primary/30 hover:text-foreground hover:-translate-y-0.5 data-[state=active]:border-transparent data-[state=active]:bg-gradient-to-r data-[state=active]:text-white data-[state=active]:shadow-lg flex-shrink-0 whitespace-nowrap"
                    data-gradient={tab.gradient}
                    style={{
                      backgroundImage: activeTab === tab.value
                        ? `linear-gradient(to right, var(--tw-gradient-stops))`
                        : undefined,
                    }}
                  >
                    <span
                      className={`absolute inset-0 rounded-full bg-gradient-to-r ${tab.gradient} opacity-0 group-data-[state=active]:opacity-100 transition-opacity duration-300 -z-0`}
                    />
                    <tab.icon className="relative z-10 h-4 w-4 sm:h-[18px] sm:w-[18px]" />
                    <span className="relative z-10">{tab.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {/* Tab Content - elegant card frame */}
            <div className="rounded-3xl border border-border bg-card shadow-xl shadow-primary/5 p-3 sm:p-5 lg:p-6">
              <TabsContent value="packing" className="mt-0 animate-fade-in">
                <PackingAssistant />
              </TabsContent>
              <TabsContent value="currency" className="mt-0 animate-fade-in">
                <CurrencyConverterWithScanner />
              </TabsContent>
              <TabsContent value="health" className="mt-0 animate-fade-in">
                <HealthWellness />
              </TabsContent>
              <TabsContent value="local-tips" className="mt-0 animate-fade-in">
                <CombinedLocalTips />
              </TabsContent>
              {hasFeature('seasonal_recommendations') && (
                <TabsContent value="seasonal" className="mt-0 animate-fade-in">
                  <SeasonalRecommendations />
                </TabsContent>
              )}
            </div>
          </Tabs>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-secondary/20 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center space-y-6">
            <SocialFollow />
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
              <button
                onClick={() => navigate('/terms')}
                className="hover:text-foreground transition-colors"
              >
                Terms of Use
              </button>
              <span className="hidden sm:inline">•</span>
              <button
                onClick={() => navigate('/privacy')}
                className="hover:text-foreground transition-colors"
              >
                Privacy Policy
              </button>
              <span className="hidden sm:inline">•</span>
              <button
                onClick={openCookieSettings}
                className="hover:text-foreground transition-colors"
              >
                Cookie Settings
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} OdyVoy. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

        {/* Demo Video */}
        <DemoVideo open={isDemoOpen} onOpenChange={setIsDemoOpen} />
        
        {/* Floating Chat Button */}
        <FloatingChatButton />
      </div>
    </>;
};
export default Dashboard;